import React, { forwardRef } from 'react';

import CloseIcon from '@mui/icons-material/Close';

import { Box, IconButton, Typography } from 'components';
import { Danger, InfoCircle, TickCircle, Warning2 } from 'iconsax-react';

import {
  CustomContentProps,
  SnackbarContent,
  useSnackbar,
  useTranslation,
} from 'third-party';

import { palette } from 'styles/theme';

type ToastVariant = 'error' | 'success' | 'warning' | 'info';

type ToastTone = {
  icon: typeof Danger;
  background: string;
};

const tones = {
  error: {
    icon: Danger,
    background: palette.error500,
    titleKey: 'common.error',
  },
  success: {
    icon: TickCircle,
    background: palette.success500,
    titleKey: 'common.success',
  },
  warning: {
    icon: Warning2,
    background: palette.warning700,
    titleKey: 'common.warning',
  },
  info: {
    icon: InfoCircle,
    background: palette.info700,
    titleKey: 'common.information',
  },
} as const satisfies Record<ToastVariant, ToastTone & { titleKey: string }>;

const isToastVariant = (variant: string): variant is ToastVariant =>
  variant in tones;

export const Toast = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message, variant, style, className }, ref) => {
    const { closeSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const tone = isToastVariant(variant) ? tones[variant] : tones.info;
    const ToneIcon = tone.icon;
    const messageId = `notistack-toast-${id}`;

    return (
      <SnackbarContent
        ref={ref}
        role="alert"
        aria-describedby={messageId}
        style={style}
        className={className}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            width: '100%',
            minWidth: 0,
            maxWidth: { xs: '100%', sm: '27rem' },
            padding: '0.875rem 0.75rem 0.875rem 1rem',
            borderRadius: '0.625rem',
            backgroundColor: tone.background,
            boxShadow:
              '0 0.5rem 1.5rem rgba(15, 23, 42, 0.24), 0 0.125rem 0.25rem rgba(15, 23, 42, 0.12)',
          }}
        >
          <Box
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: '1.25rem',
              height: '1.25rem',
              marginTop: '0.0625rem',
            }}
          >
            <ToneIcon variant="Bold" size="20" color={palette.white} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, paddingTop: '0.0625rem' }}>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                lineHeight: 1.4,
                letterSpacing: '0.01em',
                color: palette.white,
              }}
            >
              {t(tone.titleKey)}
            </Typography>
            <Typography
              id={messageId}
              sx={{
                marginTop: '0.125rem',
                fontSize: '0.8125rem',
                fontWeight: 400,
                lineHeight: 1.5,
                color: 'rgba(255, 255, 255, 0.92)',
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-line',
              }}
            >
              {message}
            </Typography>
          </Box>

          <IconButton
            size="small"
            aria-label={t('common.close')}
            onClick={() => closeSnackbar(id)}
            sx={{
              flexShrink: 0,
              padding: '0.25rem',
              marginTop: '-0.125rem',
              color: 'rgba(255, 255, 255, 0.75)',
              '&:hover': {
                color: palette.white,
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
              },
              '&:focus-visible': {
                outline: `0.125rem solid ${palette.white}`,
                outlineOffset: '0.0625rem',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: '1.125rem' }} />
          </IconButton>
        </Box>
      </SnackbarContent>
    );
  },
);

Toast.displayName = 'Toast';
